import { IPagination, IPaginationRequest } from "interfaces/pagination";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import { Button } from "@mui/material";
import Typography from "@mui/material/Typography";
import Header from "components/common/Header";
import Table from "components/common/Table";
import withAuth from "hocs/withAuth";
import withPermission from "hocs/withPermission";
import useAlert from "hooks/useAlert";
import useToken from "hooks/useToken";
import { ICobraProduct } from "interfaces/cobraProduct";
import { IPromotion } from "interfaces/promotion";
import CobraProductServices from "services/cobraProduct";
import Swal from "sweetalert2";

interface IProductData {
  id: number;
  name: string;
  category: string;
  year: string;
  season: string;
  image: string;
}

const ListProduct = () => {
  const navigate = useNavigate();
  const { token } = useToken();
  const { onAlert } = useAlert();
  const [data, setData] = useState<{
    data: IProductData[];
    pagination: IPagination;
  }>({
    data: [],
    pagination: {
      page: 1,
      total_item: 0,
      total_page: 0,
    },
  });
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<IPaginationRequest>({
    page: 1,
    per_page: 10,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchData = useCallback(() => {
    CobraProductServices.getAll(token as string, search, {
      per_page: pagination.per_page,
      page: pagination.page,
    })
      .then((res) => {
        let paginationRes: IPagination = res.data.pagination;
        setData({
          data: res.data.data.map((d: ICobraProduct) => {
            return {
              id: d.id,
              name: d.name,
              category: d.category,
              year: d.year,
              season: d.season,
              image: d.image,
            };
          }),
          pagination: {
            page: paginationRes.page,
            total_item: paginationRes.total_item,
            total_page: paginationRes.total_page,
          },
        });
        setIsLoading(false);
      })
      .catch((err) => {
        onAlert("error", err.response.data.message);
      });
  }, [token, onAlert, pagination, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDelete = (item: IPromotion) => {
    Swal.fire({
      icon: "warning",
      html: `ต้องการลบสินค้า <b>${item.name}</b> ใช่ หรือไม่?`,
      showCancelButton: true,
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        CobraProductServices.delete(token as string, item.id)
          .then((res) => {
            fetchData();
            onAlert("success", "ลบสินค้าสำเร็จ");
          })
          .catch((err) => {
            onAlert("error", err.response.data.message);
          });
      }
    });
  };

  return (
    <>
      <Header
        title="Product Master"
        breadcrumbs={[
          {
            label: "Dashboard",
            href: `/dashboard`,
          },
          {
            label: "Cobra",
            href: `/dashboard/cobra`,
          },
          {
            label: "Product Master",
            href: "/product/cobra/list",
          },
          {
            label: "List",
          },
        ]}
        actions={[
          <Link to={`/product/cobra/create`}>
            <Button variant="contained" size="large">
              <AddIcon
                sx={{
                  fontSize: 14,
                  mr: 1,
                }}
              />
              <Typography>Create</Typography>
            </Button>
          </Link>,
        ]}
      />
      <Table
        headers={[
          {
            key: "id",
            label: "No./ID",
          },
          {
            key: "name",
            label: "MODEL NAME",
          },
          {
            key: "category",
            label: "CATEGORY",
          },
          {
            key: "year",
            label: "YEAR",
          },
          {
            key: "season",
            label: "SEASON",
          },
          {
            key: "image",
            label: "IMAGE",
            render: (value: string, index: number, item: IProductData) => (
              <img
                style={{
                  borderRadius: "8px",
                  width: "100px",
                  height: "100px",
                  objectFit: "cover",
                }}
                src={process.env.REACT_APP_FILE + value}
                alt={item.name}
              />
            ),
          },
        ]}
        actions={{
          onDetail: (item: any) => navigate(`/product/cobra/${item.id}`),
          onEdit: (item: any) => navigate(`/product/cobra/${item.id}/edit`),
          onDelete: (item: any) => onDelete(item),
        }}
        onSearch={(searchText) => setSearch(searchText)}
        data={data.data}
        isLoading={isLoading}
        pagination={
          data.pagination
            ? {
                ...data.pagination,
                onChange: (page) =>
                  setPagination((pagination) => {
                    return {
                      ...pagination,
                      page,
                    };
                  }),
              }
            : undefined
        }
      />
    </>
  );
};

export default withAuth(withPermission(ListProduct, "MANAGE_COBRA"));
